import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Controller,
  ControllerRenderProps,
  FieldPath,
  RegisterOptions,
  UseFormReturn,
} from 'react-hook-form';

import { X } from 'lucide-react';

import { Button } from 'components/Button';
import { Label } from 'components/Label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'components/Tooltip';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { NormalizedLoad } from 'types/Load';
import { Maybe } from 'types/UtilityTypes';

type FormFieldPath = FieldPath<NormalizedLoad>;

type PillClassResolverArgs = {
  itemValue: string;
  isCustomValue: boolean;
  isNewValue: boolean;
};

interface MultiSelectPillInputProps<TFieldPath extends FormFieldPath> {
  name: TFieldPath;
  label: string;
  control: UseFormReturn<NormalizedLoad>['control'];
  required?: boolean;
  rules?: RegisterOptions<NormalizedLoad, TFieldPath>;
  disabled?: boolean;
  description?: string;
  optionValues?: string[];
  autoSelectFirstOption?: boolean;
  suggestionsLabel?: string;
  suggestionsTooltip?: string;
  showSuggestions?: boolean;
  hideAIHint?: boolean;
  resolveSelectedPillClasses?: (args: PillClassResolverArgs) => string;
  maxChars?: number;
}

const normalizeValueToString = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }
  if (Array.isArray(value)) {
    return value
      .map((entry) => String(entry).trim())
      .filter(Boolean)
      .join(',');
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return '';
};

export function MultiSelectPillInput<TFieldPath extends FormFieldPath>({
  name,
  label,
  control,
  required = false,
  rules,
  disabled = false,
  description,
  optionValues,
  autoSelectFirstOption = false,
  suggestionsLabel,
  suggestionsTooltip,
  showSuggestions = true,
  hideAIHint = false,
  resolveSelectedPillClasses,
  maxChars,
}: MultiSelectPillInputProps<TFieldPath>) {
  const [customValue, setCustomValue] = useState('');

  // Refs for managing the one-time initialization side effect
  const hasInitialized = useRef(false);
  const fieldRef =
    useRef<Maybe<ControllerRenderProps<NormalizedLoad, TFieldPath>>>(null);
  const customInputRef = useRef<Maybe<HTMLInputElement>>(null);
  const initialSelectedValuesRef = useRef<Maybe<Set<string>>>(null);

  const allOptions = useMemo<string[]>(() => {
    const uniqueOptions = new Set<string>();
    const addMany = (values: string[] = []) => {
      values.forEach((value) => {
        const trimmed = value.trim();
        if (trimmed) {
          uniqueOptions.add(trimmed);
        }
      });
    };

    addMany(optionValues);
    return Array.from(uniqueOptions);
  }, [optionValues]);

  useEffect(() => {
    // We only proceed if we have options, the field ref, and we haven't initialized yet.
    if (
      fieldRef.current &&
      allOptions.length > 0 &&
      !hasInitialized.current &&
      autoSelectFirstOption
    ) {
      const { value } = fieldRef.current;
      const selectedValueString = normalizeValueToString(value);

      // Only set the first option if the field is currently empty (not set by RHF defaults)
      if (!selectedValueString) {
        // Use setTimeout(0) to defer the state change until after the current render/task queue completes.
        setTimeout(() => {
          if (fieldRef.current) {
            // Re-check ref in case component unmounted
            hasInitialized.current = true;
            fieldRef.current.onChange(allOptions[0]);
          }
        }, 0);
      } else {
        // If the field already has a value (likely from defaultValues), mark as initialized
        // to prevent later runs, but don't change the value.
        hasInitialized.current = true;
      }
    }
  }, [allOptions, autoSelectFirstOption]);

  const isCustomValue = (itemValue: string) => {
    return !allOptions.includes(itemValue);
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        ...rules,
        ...(required && !rules?.required
          ? { required: 'This field is required' }
          : {}),
      }}
      render={({ field, fieldState }) => {
        // Store the field object for use in useEffect
        fieldRef.current = field as ControllerRenderProps<
          NormalizedLoad,
          TFieldPath
        >;

        const selectedValueString = normalizeValueToString(field.value);
        const selectedArray = selectedValueString
          .split(',')
          .map((item: string) => item.trim())
          .filter((item: string) => item);

        const handlePillClick = (itemValue: string) => {
          const isCurrentlySelected = selectedArray.includes(itemValue);

          const newSelectedArray = isCurrentlySelected
            ? selectedArray.filter((item) => item !== itemValue)
            : [...selectedArray, itemValue];

          field.onChange(newSelectedArray.join(','));
        };

        const handleAddCustom = () => {
          const trimmedValue = customValue.trim();
          if (trimmedValue) {
            const currentArray = selectedValueString
              .split(',')
              .map((item: string) => item.trim())
              .filter((item: string) => item);
            const valuesToAdd = trimmedValue
              .split(',')
              .map((item: string) => item.trim())
              .filter((item: string) => item);
            const newArray = [...currentArray];

            valuesToAdd.forEach((valueToAdd) => {
              if (!newArray.includes(valueToAdd)) {
                newArray.push(valueToAdd);
              }
            });

            field.onChange(newArray.join(','));

            setCustomValue('');
          }
        };

        if (!initialSelectedValuesRef.current) {
          initialSelectedValuesRef.current = new Set(selectedArray);
        }
        const initialSelectedValues = initialSelectedValuesRef.current;

        const getSelectedPillClasses = (itemValue: string) => {
          const customValueSelected = isCustomValue(itemValue);
          const isNewValue = !initialSelectedValues.has(itemValue);

          if (resolveSelectedPillClasses) {
            return resolveSelectedPillClasses({
              itemValue,
              isCustomValue: customValueSelected,
              isNewValue,
            });
          }

          return customValueSelected
            ? 'bg-neutral-100 text-neutral-700 border-neutral-200'
            : 'bg-accent-100 text-accent-700 border-accent-200';
        };

        const candidateOptions = allOptions.filter(
          (itemValue: string) => !selectedArray.includes(itemValue)
        );

        return (
          <Flex direction='col' gap='sm'>
            <Label name={name} required={required} hideAIHint={hideAIHint}>
              {label}
            </Label>
            {description && (
              <Typography variant='body-xs' className='text-neutral-500'>
                {description}
              </Typography>
            )}

            {/* Selected values container (pills in-box) */}
            <Flex
              wrap='wrap'
              align='center'
              gap='xs'
              className={`w-full rounded-md border p-1.5 ${
                disabled
                  ? 'border-neutral-input-disabled-border bg-neutral-input-disabled-bg cursor-not-allowed'
                  : 'border-neutral-input-border focus-within:border-brand-500 bg-neutral-input-bg cursor-text'
              }`}
              onClick={() => {
                if (!disabled) {
                  customInputRef.current?.focus();
                }
              }}
            >
              {selectedArray.map((itemValue: string) => (
                <Flex
                  key={itemValue}
                  align='center'
                  gap='xs'
                  className={`max-w-full border px-2 py-1 rounded-md ${getSelectedPillClasses(
                    itemValue
                  )}`}
                >
                  <Typography variant='body-xs' className='truncate'>
                    {itemValue}
                  </Typography>
                  {!disabled && (
                    <Button
                      type='button'
                      onClick={() => handlePillClick(itemValue)}
                      variant='unstyled'
                      size='auto'
                      className='text-neutral-600 hover:text-neutral-900 p-0 hover:bg-transparent'
                      title={`Remove ${label.toLowerCase()} ${itemValue}`}
                      buttonNamePosthog={null}
                    >
                      <X className='h-3 w-3' />
                    </Button>
                  )}
                </Flex>
              ))}

              {!disabled ? (
                <input
                  ref={(el) => {
                    customInputRef.current = el;
                  }}
                  type='text'
                  value={customValue}
                  onChange={(e) => setCustomValue(e.target.value)}
                  onBlur={() => {
                    handleAddCustom();
                    field.onBlur();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddCustom();
                    } else if (e.key === 'Escape') {
                      setCustomValue('');
                    } else if (
                      e.key === 'Backspace' &&
                      !customValue &&
                      selectedArray.length > 0
                    ) {
                      handlePillClick(selectedArray[selectedArray.length - 1]);
                    }
                  }}
                  placeholder={
                    selectedArray.length === 0
                      ? `Enter ${label.toLowerCase()}`
                      : `Add another ${label.toLowerCase()}`
                  }
                  className='grow p-1 outline-none rounded-[4px] text-xs min-w-[120px] bg-transparent hover:bg-neutral-input-bg text-neutral-input-text placeholder:text-neutral-input-placeholder cursor-text'
                  aria-label={`Enter ${label.toLowerCase()} values`}
                />
              ) : (
                selectedArray.length === 0 && (
                  <input
                    type='text'
                    value=''
                    disabled
                    aria-label={`${label} is disabled`}
                    className='grow p-1 outline-none rounded-[4px] text-xs min-w-[120px] bg-transparent text-neutral-input-disabled-text cursor-not-allowed'
                  />
                )
              )}
            </Flex>

            {/* Character counter */}
            {maxChars !== undefined && (
              <Typography
                variant='body-xs'
                className={
                  selectedValueString.length > maxChars
                    ? 'text-error-500'
                    : 'text-neutral-400'
                }
              >
                {selectedValueString.length} / {maxChars} characters
              </Typography>
            )}

            {/* Suggested options */}
            {showSuggestions && !disabled && candidateOptions.length > 0 && (
              <Flex direction='col' gap='xs'>
                {suggestionsLabel && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Typography
                          variant='body-xs'
                          className={`text-neutral-500 w-fit underline decoration-dotted underline-offset-2 ${
                            suggestionsTooltip ? 'cursor-help' : ''
                          }`}
                        >
                          {suggestionsLabel}
                        </Typography>
                      </TooltipTrigger>
                      {suggestionsTooltip && (
                        <TooltipContent>
                          <Typography
                            variant='body-xs'
                            className='max-w-[220px]'
                          >
                            {suggestionsTooltip}
                          </Typography>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                )}

                <Flex wrap='wrap' gap='sm'>
                  {candidateOptions.map((itemValue: string) => (
                    <Button
                      key={itemValue}
                      type='button'
                      onClick={() => handlePillClick(itemValue)}
                      variant='unstyled'
                      size='auto'
                      className={`
                        px-2 py-1 rounded-md text-xs transition-colors
                        bg-neutral-100 text-neutral-700 border border-neutral-400 hover:bg-neutral-200
                      `}
                      buttonNamePosthog={null}
                    >
                      <Typography variant='body-xs'>{itemValue}</Typography>
                    </Button>
                  ))}
                </Flex>
              </Flex>
            )}
            {fieldState.error && (
              <Typography variant='body-xs' className='text-error-500'>
                {fieldState.error.message}
              </Typography>
            )}
          </Flex>
        );
      }}
    />
  );
}
